import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import logo from '@/assets/logo.png';
import { api, setAuthToken, setUser } from '@/utils/api';
import { toast } from 'sonner';

export const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    email: '',
    password: '',
    admin_name: '',
    referral_token: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Get referral token from URL query parameter
    const refToken = searchParams.get('ref');
    if (refToken) {
      setFormData(prev => ({ ...prev, referral_token: refToken }));
    }
  }, [searchParams]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/register', formData);
      setAuthToken(response.data.access_token);
      setUser(response.data.user);
      toast.success('Welcome to VetCare!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream grid md:grid-cols-2">
      <div className="bg-terracotta-500 hidden md:flex flex-col justify-center items-center p-12 text-white">
        <div className="max-w-md">
          <img src={logo} alt="VetCare" className="h-16 w-auto mb-6" />
          <h2 className="text-4xl font-heading font-bold mb-4">Start Your Journey</h2>
          <p className="text-lg text-terracotta-50 mb-8">
            Join hundreds of veterinary clinics managing their operations with VetCare. Free to start, easy to scale.
          </p>
          <ul className="space-y-3 text-terracotta-50">
            <li className="flex items-center gap-2"><span>✓</span> Set up in minutes</li>
            <li className="flex items-center gap-2"><span>✓</span> No credit card required</li>
            <li className="flex items-center gap-2"><span>✓</span> Full access to free plan</li>
          </ul>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center gap-2 mb-4">
                <img src={logo} alt="VetCare" className="h-8 w-auto" />
                <span className="text-2xl font-heading font-bold text-sage-700">VetCare</span>
              </Link>
              <h1 className="text-3xl font-heading font-bold text-sage-800 mb-2">Create Your Clinic</h1>
              <p className="text-gray-600">Start managing your veterinary practice today</p>
              {formData.referral_token && (
                <div className="mt-3 p-3 bg-sage-50 border border-sage-200 rounded-lg">
                  <p className="text-sm text-sage-700 font-medium">🎉 You're signing up via a referral link!</p>
                </div>
              )}
            </div>

            <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
              <div>
                <Label htmlFor="name">Clinic Name</Label>
                <Input
                  id="name"
                  placeholder="Happy Paws Veterinary"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="register-clinic-name-input"
                />
              </div>
              <div>
                <Label htmlFor="address">Clinic Address</Label>
                <Input
                  id="address"
                  placeholder="123 Main St, City, State"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="register-clinic-address-input"
                />
              </div>
              <div>
                <Label htmlFor="admin_name">Your Full Name</Label>
                <Input
                  id="admin_name"
                  placeholder="Dr. John Smith"
                  value={formData.admin_name}
                  onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="register-admin-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  minLength="6"
                  data-testid="register-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full rounded-full py-6 bg-sage-500 hover:bg-sage-600 text-white shadow-lg"
                disabled={loading}
                data-testid="register-submit-button"
              >
                {loading ? 'Creating Account...' : 'Create Clinic Account'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-sage-600 font-semibold hover:text-sage-700" data-testid="login-link">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
